import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { PanelControls } from 'apps/studio/components/shared/PanelControls'
import {
  FieldSettings,
  getFieldSettingsConfig,
} from 'apps/studio/components/settings/fields'
import {
  PageSettings,
  pageSettingsConfig,
} from 'apps/studio/components/settings/page'
import {
  TableSettings,
  tableSettingsConfig,
} from 'apps/studio/components/settings/table'
import {
  GlobalSettings,
  globalSettingsConfig,
} from 'apps/studio/components/settings/global'
import {
  activeModeSelector,
} from 'apps/studio/store/selectors/studioSettings'
import{
  activeEntryGuidSelector,
  activeSheetIdSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import {
  activeEntrySelector,
  activeSheetSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { PDF_MODE } from 'apps/studio/store/reducers/settings/studioSettings'

const RightPanel = ({
  activePanels,
  activeEntry,
  activeSheet,
  isTable,
}) => {
  let PanelComponent = GlobalSettings

  if (activeEntry) {
    PanelComponent = FieldSettings
  } else if (isTable) {
    PanelComponent = TableSettings
  } else if (activeSheet) {
    PanelComponent = PageSettings
  }

  return (
    <div className="right-panel">
      <PanelComponent activePanels={activePanels} />
    </div>
  )
}

export const RightPanels = () => {
  const showWorkspacePanels = useSelector(activeModeSelector) === PDF_MODE
  const activeEntry = useSelector(activeEntrySelector)
  const activeSheet = useSelector(activeSheetSelector)
  const activeEntryGuid = useSelector(activeEntryGuidSelector)
  const activeSheetId = useSelector(activeSheetIdSelector)
  const [activeSettingsConfig, setActiveSettingsConfig] =
    useState(globalSettingsConfig)

  const [panelVisibility, setPanelVisibility] = useState({
    GlobalGlobal: true,
    GlobalTypography: true,
  })
  
  const isTable = [15, 13].includes(activeSheet?.sheet_type_id)

  // Global, Page, Table, Field
  const currentContext = activeEntry
    ? 'Field'
    : activeSheet
      ? (isTable ? 'Table' : 'Page')
      : 'Global';

  useEffect(() => {
    let updatedPanels = []
    if (activeEntry) {
      updatedPanels = getFieldSettingsConfig(activeEntry)
    } else if (isTable) {
      updatedPanels = tableSettingsConfig
    } else if (activeSheet) {
      updatedPanels = pageSettingsConfig
    } else {
      updatedPanels = globalSettingsConfig
    }
    setActiveSettingsConfig(updatedPanels)
  }, [activeEntryGuid, activeEntry?.type, activeSheetId, panelVisibility, currentContext])

  const activePanels = useMemo(() => {
    return activeSettingsConfig.reduce((active, panel) => {
      const contextualKey = `${currentContext}${panel.label}`
      // active by default
      if (panelVisibility[contextualKey] !== false) {
        active.push(panel.label)
      }
      return active
    }, [])
  }, [activeEntryGuid, activeEntry?.type, activeSheetId, activeSettingsConfig, panelVisibility, currentContext])

  const rightPanelControls = useMemo(() => {
    return activeSettingsConfig.map(panel => {
      const { label, icon } = panel
      const contextualKey = `${currentContext}${label}`

      // Active if not an override set to false
      const isActive = panelVisibility[contextualKey] !== false
      
      return {
        active: isActive,
        type: label,
        icon,
      }
    })
  }, [
    activeEntryGuid,
    activeEntry?.type,
    activeSheetId,
    activeSettingsConfig,
    panelVisibility,
    currentContext
  ])

  const togglePanel = panelName => {
    const contextualKey = `${currentContext}${panelName}`
    const currentlyActive = panelVisibility[contextualKey] !== false

    setPanelVisibility(prevState => ({
      ...prevState,
      [contextualKey]: !currentlyActive
    }))
  }

  return (
    <>
      <PanelControls
        side="right"
        togglePanelAction={togglePanel}
        showPanelControls={showWorkspacePanels}
        controls={rightPanelControls}
      />
      {showWorkspacePanels && (
        <RightPanel
          activePanels={activePanels}
          activeEntry={activeEntry}
          activeSheet={activeSheet}
          isTable={isTable}
        />
      )}
    </>
  )
}
